import SubHeaderContent from '@/components/MobileApproval/SubHeaderContent';
import ApprovalContents from '@/components/MobileApproval/ApprovalContents';
import { useSearchApproval } from '@/hooks/apiHooks';
import useSWRInfinite from 'swr/infinite';
import { Approval, TotalApprovalParams } from '@/types/api';
import { getApprovalkey, MAX_LIST_SIZE } from '@/components/MobileApproval';
import { APPROVAL_SEARCH_URL } from '@/constant/approval';
import { approvalSearchListFetcher } from '@/hooks/apiHooks/approval';
import Loading from '@/components/MobileApproval/Body/Loading';
import { useAtom, useAtomValue } from 'jotai';
import { approvalState, MyInfoState } from '@/recoil/atom';
import { useTranslation } from 'react-i18next';
import ApprovalSkeleton from '@/components/MobileApproval/ApprovalSkeleton';
import ErrorMessage from '@/components/MobileSales/ErrorMessage';
import React, { useEffect, useState } from 'react';
import { overCountText } from '@/utils';
import { MAX_APPROVAL_LIST_SIZE } from '@/components/Approval';
import { SubHeaderContainer } from '@/components/MobileApproval/Body/styles';
import { ContentLayout } from '@/components/MobileApproval/ApprovalContents/styles';

type P = {
  showHeader: boolean;
};

const MyWrite = ({ showHeader }: P) => {
  const [selectedState, setSelectedState] = useAtom(approvalState);
  const [isRn, setIsRn] = useState(false);

  const { t } = useTranslation();
  const { passportId } = useAtomValue(MyInfoState);
  const idxToStatus = (idx: number): TotalApprovalParams | undefined => {
    switch (idx) {
      case 0:
        return { authorPassportIds: [passportId], statuses: ['P'] };
      case 1:
        return { authorPassportIds: [passportId], statuses: ['S'] };
      case 2:
        return { authorPassportIds: [passportId], statuses: ['C'] };
      case 3:
        return { authorPassportIds: [passportId], statuses: ['R'] };
    }
  };

  useEffect(() => {
    window?.isRNWebview && setIsRn(true);
  }, []);

  const { data: countData } = useSearchApproval({
    listSize: MAX_APPROVAL_LIST_SIZE,
    sortType: 'authoredAt',
    orderType: 'desc',
    authorPassportIds: [passportId],
    statuses: ['P'],
  });

  const { data, size, setSize, isLoading, mutate, error } = useSWRInfinite<
    Approval[]
  >(
    getApprovalkey(APPROVAL_SEARCH_URL, {
      listSize: MAX_LIST_SIZE,
      // 임시저장(myWriteKey 1, status 'S') 탭은 authoredAt 이 대부분 null 이라
      // 실제 수정일(updatedAt) 기준으로 정렬해 최근 작업분이 위로 오게 한다.
      sortType: selectedState.myWriteKey === 1 ? 'updatedAt' : 'authoredAt',
      orderType: 'desc',
      ...idxToStatus(selectedState.myWriteKey),
    }),
    approvalSearchListFetcher<Approval>,
    {
      refreshInterval: isRn ? 1000 : 0,
      shouldRetryOnError: false, // 에러 발생 시 재시도를 하지 않도록 설정
      revalidateOnFocus: false,
    },
  );

  const list = [
    `${t('진행중')}${` ${overCountText(countData?.meta.totalCount) ?? ''}`}`,
    t('임시저장'),
    t('완료'),
    t('반려'),
  ];

  const handleClick = (id: number) =>
    setSelectedState({
      ...selectedState,
      myWriteKey: id,
    });

  const resultData = data?.reduce((prev, next) => prev.concat(next));

  const readMoreData = () => {
    setSize(size + 1);
  };

  const isReachingEnd = data && data[data.length - 1]?.length < MAX_LIST_SIZE;

  const handleRefresh = () => mutate();

  return (
    <>
      <SubHeaderContainer showHeader={showHeader}>
        {list.map((res, idx) => (
          <SubHeaderContent
            key={`subHeader-${idx}`}
            id={idx}
            title={res}
            clicked={selectedState.myWriteKey === idx}
            handleClick={handleClick}
          />
        ))}
      </SubHeaderContainer>
      <ContentLayout haveSubHeader={true}>
        {isLoading ? (
          <>
            {Array.from({ length: 10 }, (_, i) => (
              <ApprovalSkeleton key={`ApprovalSkeleton-${i}`} />
            ))}
          </>
        ) : error ? (
          <ErrorMessage
            errorMessage={error?.response?.data?.message}
            errorCode={error?.response?.data?.status}
            refresh={handleRefresh}
          />
        ) : (
          <div>
            <ApprovalContents data={resultData} onRefresh={handleRefresh} />
          </div>
        )}
        {data && !isLoading && !isReachingEnd && (
          <Loading readMoreData={readMoreData} />
        )}
      </ContentLayout>
    </>
  );
};
export default MyWrite;
